import * as React from "react"
import { useState } from "react"
import { setOutSideFormField } from "./Helper"
import { Modal, Button } from 'react-bootstrap';

type Emotion = {
  name: string | undefined,
  value: string | undefined,
}

interface Props {
  emotions: Emotion[],
  tag: string,
  handleChange: (attribute: string, id: number, value: string) => void,
  updateEmotion: (emotion: Emotion[]) => void,
  labelName: string,
  iconClass: string,
  isAfter: boolean,
  beforeEmotions?: Emotion[],
}

const getEmotionId = (input: string) => parseInt(input.split("_")[2])

const constructEmotion = (emotionStr: string | null) => {
  if (emotionStr === null || emotionStr === "") {
    return [{ name: undefined, value: undefined}];
  } else {
    return JSON.parse(emotionStr);
  }
}

const ALL_EMOTION_NAME_OPTIONS: { [key:string] : string[] } = {
  "喜び": ["楽しい", "嬉しい", "幸せ", "安心", "満足", "リラックス"],
  "怒り": ["怒り", "イライラ", "憎い", "不公平", "嫉妬", "不満"],
  "不愉快": ["ゆううつ", "苦しい", "退屈", "ダルい", "困惑", "自己嫌悪"],
  "恐れ": ["不安", "怖い", "焦り", "緊張", "恥ずかしい", "罪悪感"],
  "悲しみ": ["悲しい", "寂しい", "虚しい", "情けない", "後悔", "絶望"],
};

//sad,anxiety,fear,anger,happy
const getEmotionCSSClass = (name: string) => {
  switch (name) {
    case "喜び":
    case "楽しい":
    case "嬉しい":
    case "幸せ":
    case "安心":
    case "満足": 
    case "リラックス":
      return "happy";

    case "怒り":
    case "イライラ":
    case "憎い":
    case "不公平":
    case "嫉妬":
    case "不満":
      return "anger";
    
    case "不愉快":
    case "ゆううつ":
    case "苦しい":
    case "退屈":
    case "ダルい":
    case "困惑":
    case "自己嫌悪":
      return "anxiety";
    
    case "恐れ":
    case "不安":
    case "怖い":
    case "焦り":
    case "緊張":
    case "恥ずかしい":
    case "罪悪感":
      return "fear";
    
    case "悲しみ":
    case "悲しい":
    case "寂しい":
    case "虚しい":
    case "情けない":
    case "後悔":
    case "絶望":
      return "sad";
    
    default:
      return "";
  }
}

const ALL_EMOTION_VALUE_OPTIONS = ["10", "20", "30", "40", "50", "60", "70", "80", "90", "100"];

const EmotionField: React.FC<Props> = (props) => {
  const [modalShow, setModalShow] = useState(false)
  const [currentEmotionId, setCurrentEmotionId] = useState(0)

  const emotions = props.emotions;

  const openDeleteModal = (event: React.SyntheticEvent) => {
    const target = event.target as HTMLInputElement;
    setModalShow(true);
    setCurrentEmotionId(parseInt(target.getAttribute('data-value')!!));
    event.preventDefault();
  }

  const closeDeleteModal = (event: React.SyntheticEvent) => {
    setModalShow(false)
    event.preventDefault();
  }

  const addEmotion = (event: React.SyntheticEvent) => {
    props.updateEmotion([...props.emotions, { name: undefined, value: undefined}]);
    setOutSideFormField(props.tag, JSON.stringify(emotions));
    event.preventDefault();
  }

  const deleteEmotion = (event: React.SyntheticEvent) => {
    let newEmotion = props.emotions.filter((_, index) => index !== currentEmotionId)
    if (newEmotion.length === 0) {
      newEmotion.push({name: undefined, value: undefined})
    }
    props.updateEmotion(newEmotion);
    setModalShow(false);
    event.preventDefault();
  }

  const createOption = (options: string[], key: string) => (
    options.map(name => (<option key={`${key}_${name}`} value={name}>{name}</option>))
  )

  const createEmotionList = (options: string[], key: string, selected: string) => (
    options.map(name => (
      <li key={`${key}_${name}`} value={name} className={`${name === selected ? 'selected' : ''}`} onClick={ () => handleNameOnClick(key, name)}>{name}</li>
      ))
  )

  const inBeforeEmotion = (name: string | undefined) => props.beforeEmotions && name && props.beforeEmotions.some( (e) => e.name === name)

  const handleValueOnClick = (event: React.SyntheticEvent) => {
    const target = event.target as HTMLInputElement;
    const id = getEmotionId(target.name);
    props.handleChange("value", id, target.value)
  }

  const handleNameOnClick = (key: string, value: string) => {
    const id = getEmotionId(key)
    props.handleChange("name", id, value)
  }

  const createEmotionForm = (name: string | undefined, value :string | undefined, id: number) => (
    <div key={`emotion_${id}`} className="form-group emotion-form-layout">
      <div>
        { props.isAfter && inBeforeEmotion(name)
          ? <div className="d-flex justify-content-between">
              <span className={`emotion-icon ${getEmotionCSSClass(name!!)}`}>{ name }</span>
              <span className="emotion-value">{ (props.beforeEmotions === undefined || props.beforeEmotions[id] === undefined || props.beforeEmotions[id].value === undefined) ? '' : `${props.beforeEmotions[id].value}%` }</span>
            </div>
          : <div className="dropdown">
              <button className="select form-control dropdown-toggle w-100 text-left" type="button" id={`dropdownMenuButton_${id}`} data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                {name ? <span className={`emotion-icon ${getEmotionCSSClass(name!!)}`}>{name}</span> : <span>選択してください</span> }
              </button>
              <div className="dropdown-menu" aria-labelledby={`dropdownMenuButton_${id}`} >
                <div className="emotion-dropdown" >
                  {Object.keys(ALL_EMOTION_NAME_OPTIONS).map( k => (
                    <div>
                      <div className={`emotion-dropdown-key emotion-icon ${getEmotionCSSClass(k)}`}>{k}</div>
                      <ul className="list-unstyled emotion-dropdown-list">
                        {createEmotionList(ALL_EMOTION_NAME_OPTIONS[k], `emotion_name_${id}`, `${name}`)}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>
        }
      </div>

      <div>
        <select className="form-control" key={`emotion_value_${id}`} value={value} name={`emotion_value_${id}`} onChange={handleValueOnClick}>
          <option key={`emotion_value_${id}_undefined`} value={undefined}>--</option>
          {createOption(ALL_EMOTION_VALUE_OPTIONS, `emotion_value_${id}`)}
        </select>
        <span className="ml-1 d-none d-md-inline">%</span>
      </div>

      <button className="btn-icon danger" onClick={openDeleteModal} title="削除">
        <span className="icon-trash-2" data-value={id}></span>
      </button>
    </div>
  )

  const deleteModalDialog = () => (
    <Modal show={modalShow} onHide={closeDeleteModal}>
      <Modal.Header closeButton>
        <Modal.Title>削除確認</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>この項目を削除してもよろしいですか？</p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={closeDeleteModal}>
          キャンセル
        </Button>
        <Button variant="danger" onClick={deleteEmotion}>
          削除
        </Button>
      </Modal.Footer>
    </Modal>
  )

  return (
    <div className="column-form">
      <div className="column-form-icon">
      <span className={props.iconClass + ' column-data-icon'}></span>
        <span className="column-data-icontext">{props.labelName}</span>
      </div>
      <div>
        { props.isAfter ? <label className="d-block">別の考えをすると少しでも気持ちの強さは変わりましたか？</label> : 
          <div className="emotion-form-layout">
            <label className="d-block">どんな気持ちでしたか？</label>
            <label className="d-block">気持ちの強さは？</label>
          </div>
        }
        {props.emotions.map((emotion, index) => createEmotionForm(emotion.name, emotion.value, index))}
        { props.emotions.length < 3 &&
          <a className="text-icon link" onClick={addEmotion}>
            <span className="icon-plus"></span>追加 (最大3つ)
          </a>
        } 
      </div>
      {deleteModalDialog()}
    </div>
  )
}

export {
  EmotionField,
  Emotion,
  constructEmotion,
  getEmotionCSSClass,
}
